import { type FC, useContext } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { NftProfileSectionLoadMore } from './NftProfileSectionLoadMore.tsx';
import { trackPdpImpression, trackPdpRedirect } from './nftCollectorTracking.ts';
import { ServerVarsContext } from '../../../../utils/ServerVarsContext.tsx';

import styles from './NftProfileSections.scss';

import { type NftProfileSectionCreations_viewer$key as ViewerRef } from './__generated__/NftProfileSectionCreations_viewer.graphql.ts';
import { type NftProfileSectionCreationsPaginationQuery as PaginationQuery } from './__generated__/NftProfileSectionCreationsPaginationQuery.graphql.ts';

type Props = {
    viewer: ViewerRef;
    hasImports?: boolean;
};

export const NftProfileSectionCreations: FC<Props> = ({ hasImports, viewer: viewerRef }) => {
    const isMobile = !!useContext(ServerVarsContext).isMobile;
    const { currency } = useCurrencyStore();
    const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
        PaginationQuery,
        ViewerRef
    >(
        graphql`
            fragment NftProfileSectionCreations_viewer on Viewer
            @refetchable(queryName: "NftProfileSectionCreationsPaginationQuery") {
                nftCreations: nftItemSearch(
                    creatorProfileUsernames: [$username]
                    creatorAddresses: [$walletAddress]
                    onlyFirstdibsContracts: true
                    first: $creationsFirst
                    after: $creationsCursor
                ) @connection(key: "NftProfileSectionCreations_nftCreations") {
                    edges {
                        node {
                            serviceId
                            metadata {
                                ... on NftDigitalArtMetadata {
                                    name
                                }
                            }
                            mostRecentAuction(excludeCanceled: true) {
                                ...NftItemTile_nftAuction
                            }
                            ...NftItemTile_nftItem
                        }
                    }
                }
                ...NftItemTile_viewer
                ...NftHeartPillProvider_viewer
            }
        `,
        viewerRef
    );

    return (
        <NftHeartPillProvider
            viewer={data}
            nftItemIds={(data.nftCreations?.edges || [])
                .map(edge => edge?.node?.serviceId || null)
                .filter(filterNulls)}
        >
            {hasImports && <div className={styles.divider} />}
            <ul className={styles.dibsCollection}>
                {data.nftCreations?.edges?.map((edge, index) => {
                    const nftItem = edge?.node;

                    if (!nftItem) {
                        return null;
                    }

                    return (
                        <li className={styles.itemTileWrapper} key={nftItem.serviceId}>
                            <NftItemTile
                                tileType="COLLECTOR_TILE_NO_CREATOR"
                                nftItem={nftItem}
                                nftAuction={nftItem.mostRecentAuction}
                                fireTracking={trackPdpRedirect(
                                    nftItem.serviceId,
                                    nftItem.metadata?.name,
                                    index,
                                    'CREATOR_ITEMS_TAB'
                                )}
                                fireImpressionTracking={trackPdpImpression(
                                    nftItem.serviceId,
                                    nftItem.metadata?.name,
                                    index,
                                    'CREATOR_ITEMS_TAB'
                                )}
                                key={nftItem.serviceId}
                                viewer={data}
                                currency={currency}
                                gaLabel="NFT-Collector"
                                isMobile={isMobile}
                            />
                        </li>
                    );
                })}
            </ul>
            <NftProfileSectionLoadMore
                hasNext={hasNext}
                isLoadingNext={isLoadingNext}
                onClick={() => loadNext(20)}
            />
        </NftHeartPillProvider>
    );
};
